import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export interface LogEntry {
  id: string;
  admin_email: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  old_value: any;
  new_value: any;
  description: string;
  created_at: string;
}

interface Filters {
  adminEmail?: string;
  action?: string;
  dateRange?: 'today' | 'week' | 'month' | 'all';
  search?: string;
}

export function useLogs(filters?: Filters) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('admin_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);

      if (filters?.adminEmail) query = query.eq('admin_email', filters.adminEmail);
      if (filters?.action) query = query.eq('action', filters.action);

      if (filters?.dateRange === 'today') {
        const today = new Date().toISOString().split('T')[0];
        query = query.gte('created_at', today + 'T00:00:00');
      } else if (filters?.dateRange === 'week') {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        query = query.gte('created_at', d.toISOString());
      } else if (filters?.dateRange === 'month') {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        query = query.gte('created_at', d.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      let result = data || [];

      if (filters?.search) {
        const s = filters.search.toLowerCase();
        result = result.filter(l =>
          (l.description || '').toLowerCase().includes(s) ||
          (l.admin_email || '').toLowerCase().includes(s)
        );
      }

      setLogs(result);
    } catch (err) {
      console.error('Fetch logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters?.adminEmail, filters?.action, filters?.dateRange, filters?.search]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return { logs, loading, refetch: fetchLogs };
}
